import { useState, useEffect, useCallback, useRef } from "react";
import { useSignalR } from "../Contexts/SignalRContext";
import apiClient from "../Helpers/apiClient";

// Define workflow stages
export const WORKFLOW_STAGES = {
    CALCULATE_PRICE: "calculate_price",
    GENERATE_MODEL: "generate_model",
    DOWNLOAD_AND_QUOTE: "download_and_quote",
};

const STORAGE_KEY = "ecoProtectorState";
const HANDLER_ID = "ecoProtector";

export const useEcoProtectorState = (QuoteId, quantity, showNotification) => {
    const {
        connection,
        connectionId,
        isConnected,
        registerGlobalHandler,
        unregisterGlobalHandler,
        getPendingMessages,
    } = useSignalR();

    // Component state
    const [currentStage, setCurrentStage] = useState(WORKFLOW_STAGES.CALCULATE_PRICE);
    const [activeStep, setActiveStep] = useState(0);
    const [isGenerating, setIsGenerating] = useState(false);
    const [totalPrice, setTotalPrice] = useState(0);
    const [priceBreakdown, setPriceBreakdown] = useState(null);
    const [viewerPath, setViewerPath] = useState(
        `${process.env.REACT_APP_API_URL}/StdModel/output/1/result.svf`
    );
    const [downloadUrls, setDownloadUrls] = useState({
        pdf: "",
        dwg: "",
        dwg3d: "",
    });
    const [modelConfig, setModelConfig] = useState({
        modelType: "ECP.1200",
        finishSurfaceLevel: 100,
        inletIL: 98,
        connectionSize: "",
        connectionType: "",
        coverType: "Class E",
        city: "",
        riserHeight: 800,
        workItemId: "",
    });

    useEffect(() => {
        console.log("🔄 modelConfig changed:", modelConfig);
    }, [modelConfig]);

    // Store the latest QuoteId and quantity in state to ensure it's available for pending message processing
    const [storedQuoteId, setStoredQuoteId] = useState(QuoteId);
    const [storedQuantity, setStoredQuantity] = useState(quantity);

    // Update stored values when props change
    useEffect(() => {
        console.log("🔄 EcoProtector: Updating stored QuoteId and quantity:", { QuoteId, quantity });
        setStoredQuoteId(QuoteId);
        setStoredQuantity(quantity);
    }, [QuoteId, quantity]);

    // Use refs to maintain current values in async callbacks
    const currentStateRef = useRef({
        QuoteId: storedQuoteId,
        quantity: storedQuantity,
        totalPrice,
        modelConfig,
        currentStage,
        activeStep,
        isGenerating,
        viewerPath,
        downloadUrls,
        priceBreakdown,
    });

    // Update ref whenever state changes
    useEffect(() => {
        currentStateRef.current = {
            QuoteId: storedQuoteId,
            quantity: storedQuantity,
            totalPrice,
            modelConfig,
            currentStage,
            activeStep,
            isGenerating,
            viewerPath,
            downloadUrls,
            priceBreakdown,
        };
    }, [
        storedQuoteId,
        storedQuantity,
        totalPrice,
        modelConfig,
        currentStage,
        activeStep,
        isGenerating,
        viewerPath,
        downloadUrls,
        priceBreakdown,
    ]);

    // Save state to sessionStorage
    const saveStateToLocalStorage = useCallback(() => {
        const stateToSave = {
            currentStage: currentStateRef.current.currentStage,
            activeStep: currentStateRef.current.activeStep,
            viewerPath: currentStateRef.current.viewerPath,
            downloadUrls: currentStateRef.current.downloadUrls,
            modelConfig: currentStateRef.current.modelConfig,
            totalPrice: currentStateRef.current.totalPrice,
            priceBreakdown: currentStateRef.current.priceBreakdown,
            isGenerating: currentStateRef.current.isGenerating,
            storedQuoteId: currentStateRef.current.QuoteId,
            storedQuantity: currentStateRef.current.quantity,
            lastSaved: Date.now(),
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
        console.log("💾 EcoProtector: State saved to sessionStorage:", {
            stage: stateToSave.currentStage,
            step: stateToSave.activeStep,
            isGenerating: stateToSave.isGenerating,
            workItemId: stateToSave.modelConfig?.workItemId,
            hasDownloadUrls: Object.values(stateToSave.downloadUrls).some((url) => !!url),
            quoteId: stateToSave.storedQuoteId,
            quantity: stateToSave.storedQuantity,
            timestamp: new Date(stateToSave.lastSaved).toISOString(),
        });
    }, []);

    // Restore state from sessionStorage
    const restoreStateFromLocalStorage = useCallback(() => {
        console.log("🔍 EcoProtector: Attempting to restore state from sessionStorage...");
        try {
            const savedState = sessionStorage.getItem(STORAGE_KEY);
            if (!savedState) {
                console.log("✅ EcoProtector: No saved state found");
                return false;
            }

            const parsedState = JSON.parse(savedState);

            // Check if the saved state is not too old (optional: 24 hours)
            const maxAge = 24 * 60 * 60 * 1000; // 24 hours
            const age = Date.now() - parsedState.lastSaved;
            if (age > maxAge) {
                console.log(
                    `🗑️ EcoProtector: Saved state is too old (${Math.round(
                        age / 60000
                    )} minutes), starting fresh`
                );
                sessionStorage.removeItem(STORAGE_KEY);
                return false;
            }

            console.log("🔄 EcoProtector: Restoring state from sessionStorage:", {
                stage: parsedState.currentStage,
                step: parsedState.activeStep,
                isGenerating: parsedState.isGenerating,
                workItemId: parsedState.modelConfig?.workItemId,
                hasDownloadUrls: Object.values(parsedState.downloadUrls).some((url) => !!url),
                quoteId: parsedState.storedQuoteId,
                quantity: parsedState.storedQuantity,
                ageMinutes: Math.round(age / 60000),
                savedAt: new Date(parsedState.lastSaved).toISOString(),
            });

            setCurrentStage(parsedState.currentStage);
            setActiveStep(parsedState.activeStep);
            setViewerPath(parsedState.viewerPath);
            setDownloadUrls(parsedState.downloadUrls);
            setModelConfig(parsedState.modelConfig);
            setTotalPrice(parsedState.totalPrice);
            setPriceBreakdown(parsedState.priceBreakdown);
            setIsGenerating(parsedState.isGenerating);

            // Restore the stored QuoteId and quantity
            if (parsedState.storedQuoteId) {
                setStoredQuoteId(parsedState.storedQuoteId);
            }
            if (parsedState.storedQuantity) {
                setStoredQuantity(parsedState.storedQuantity);
            }

            console.log("✅ EcoProtector: State restored successfully");
            return true;
        } catch (error) {
            console.error("❌ EcoProtector: Error restoring state from sessionStorage:", error);
            sessionStorage.removeItem(STORAGE_KEY);
            return false;
        }
    }, []);

    // Generate EcoProtector description PDF
    const generateEcoprotectorDescriptionPdf = useCallback(
        async (workItemId) => {
            console.log("🎯 EcoProtector: Starting PDF generation for workItemId:", workItemId);
            console.log("📊 EcoProtector: Current state ref:", currentStateRef.current);

            // Check if we have the required data
            if (!currentStateRef.current.QuoteId) {
                console.log("⚠️ EcoProtector: QuoteId is missing, will retry PDF generation in 2 seconds");
                setTimeout(() => {
                    if (currentStateRef.current.QuoteId) {
                        console.log(
                            "🔄 EcoProtector: Retrying PDF generation with QuoteId:",
                            currentStateRef.current.QuoteId
                        );
                        generateEcoprotectorDescriptionPdf(workItemId);
                    } else {
                        console.log(
                            "❌ EcoProtector: QuoteId still missing, proceeding without PDF generation"
                        );
                        showNotification(
                            "Model generated successfully, but PDF generation was skipped due to missing QuoteId. You can still download files and add to quote.",
                            "warning"
                        );
                        // Move to download stage anyway
                        setCurrentStage(WORKFLOW_STAGES.DOWNLOAD_AND_QUOTE);
                        setActiveStep(2);
                        setIsGenerating(false);
                        sessionStorage.removeItem(STORAGE_KEY);
                    }
                }, 2000);
                return;
            }

            try {
                const inputdata = {
                    ...currentStateRef.current.modelConfig,
                    QuoteId: currentStateRef.current.QuoteId,
                    TemplateId: "892243f1-f2b2-f011-bbd3-000d3ad0ff6e",
                    dataverseProductPrice: currentStateRef.current.totalPrice || 0,
                    dataveserQuoteProductQuantity: currentStateRef.current.quantity || 1,
                    workItemId: workItemId,
                };

                console.log("📤 EcoProtector: Sending PDF generation request with data:", inputdata);
                const response = await apiClient.post(
                    "/api/pdf/generate-ecoprotector-description-pdf",
                    inputdata
                );

                console.log("✅ EcoProtector: PDF generation successful, moving to download stage");
                showNotification(
                    "Model and PDF generated successfully! You can now download files and add to quote.",
                    "success"
                );

                // Move to download stage
                setCurrentStage(WORKFLOW_STAGES.DOWNLOAD_AND_QUOTE);
                setActiveStep(2);
                setIsGenerating(false);

                // Clear the saved state since we're done
                console.log("🧹 EcoProtector: Clearing saved state after successful completion");
                sessionStorage.removeItem(STORAGE_KEY);
            } catch (err) {
                console.error("❌ EcoProtector: PDF generation error:", err);
                console.error("❌ EcoProtector: Error details:", {
                    message: err.message,
                    response: err.response?.data,
                    status: err.response?.status,
                });

                // Check if it's a QuoteId validation error
                if (err.response?.status === 400 && err.response?.data?.errors?.QuoteId) {
                    console.log("🔄 EcoProtector: QuoteId validation error, will try to proceed without PDF");
                    showNotification(
                        "Model generated successfully, but PDF generation failed due to missing QuoteId. You can still download files and add to quote.",
                        "warning"
                    );
                    // Move to download stage anyway
                    setCurrentStage(WORKFLOW_STAGES.DOWNLOAD_AND_QUOTE);
                    setActiveStep(2);
                    setIsGenerating(false);
                    sessionStorage.removeItem(STORAGE_KEY);
                } else {
                    showNotification(
                        err.response?.data?.message ||
                            "Failed to generate PDF, but model was created successfully",
                        "error"
                    );
                    setIsGenerating(false);
                    sessionStorage.removeItem(STORAGE_KEY);
                }
            }
        },
        [showNotification]
    );

    // Handle onComplete SignalR event
    const handleOnComplete = useCallback(() => {
        console.log("📨 EcoProtector: Received onComplete event");
        // onComplete doesn't do much in the current implementation
    }, []);

    // Handle onComponents SignalR event
    const handleOnComponents = useCallback(
        async (message) => {
            console.log("📨 EcoProtector: Received onComponents event:", message);
            console.log("📊 EcoProtector: Current component state when processing:", {
                currentStage: currentStateRef.current.currentStage,
                isGenerating: currentStateRef.current.isGenerating,
                totalPrice: currentStateRef.current.totalPrice,
                QuoteId: currentStateRef.current.QuoteId,
                quantity: currentStateRef.current.quantity,
            });

            const baseUrl = process.env.REACT_APP_API_URL;
            const lastPartIndex = message.lastIndexOf("/output/");
            const basePath = message.substring(0, lastPartIndex);
            const workitemId = message.split("Workitems/")[1].split("/")[0];

            const newViewerPath = `${baseUrl}/${message}`;
            const newDownloadUrls = {
                pdf: `${baseUrl}/${basePath}/1200 Compete.pdf`,
                dwg: `${baseUrl}/${basePath}/1200 Compete.dwg`,
                dwg3d: `${baseUrl}/${basePath}/1200 Compete._3D.dwg`,
            };

            console.log("🔄 EcoProtector: Updating state with new paths:", {
                workitemId,
                newViewerPath,
                newDownloadUrls,
            });

            // Update state
            setViewerPath(newViewerPath);
            setDownloadUrls(newDownloadUrls);
            setModelConfig((prev) => ({
                ...prev,
                workItemId: workitemId,
            }));

            // Save updated state immediately
            setTimeout(() => {
                console.log("💾 EcoProtector: Saving state after onComponents processing");
                saveStateToLocalStorage();
            }, 100);

            // Generate PDF
            console.log("🎯 EcoProtector: Starting PDF generation...");
            await generateEcoprotectorDescriptionPdf(workitemId);
        },
        [generateEcoprotectorDescriptionPdf, saveStateToLocalStorage]
    );

    // Register/unregister SignalR handlers with type-aware message handling
    useEffect(() => {
        if (!isConnected) {
            console.log("⏳ EcoProtector: Waiting for SignalR connection...");
            return;
        }

        console.log("🔧 EcoProtector: Registering SignalR handlers");
        registerGlobalHandler("onComplete", HANDLER_ID, handleOnComplete);
        registerGlobalHandler("onComponents", HANDLER_ID, handleOnComponents);

        // Check for pending messages when the component mounts or reconnects
        const checkPendingMessages = () => {
            console.log("🔍 EcoProtector: Checking for pending messages...");
            console.log("📊 EcoProtector: Current QuoteId and quantity:", {
                QuoteId: currentStateRef.current.QuoteId,
                quantity: currentStateRef.current.quantity,
            });

            // Get pending messages for EcoProtector workitem type only
            const pendingComplete = getPendingMessages("onComplete", "ecoProtector");
            const pendingComponents = getPendingMessages("onComponents", "ecoProtector");

            console.log("📥 EcoProtector: Found pending messages:", {
                onComplete: pendingComplete.length,
                onComponents: pendingComponents.length,
            });

            // Process pending onComplete messages
            if (pendingComplete.length > 0) {
                console.log("🎯 EcoProtector: Processing pending onComplete messages");
                pendingComplete.forEach(({ data, timestamp, id }) => {
                    console.log(
                        `📨 EcoProtector: Processing onComplete message ${id} from ${new Date(
                            timestamp
                        ).toISOString()}`
                    );
                    handleOnComplete(data);
                });
            }

            // Process pending onComponents messages (most recent first)
            if (pendingComponents.length > 0) {
                console.log("🎯 EcoProtector: Processing pending onComponents messages");
                // Take the most recent message
                const mostRecent = pendingComponents[pendingComponents.length - 1];
                console.log(
                    `📨 EcoProtector: Processing most recent onComponents message ${
                        mostRecent.id
                    } from ${new Date(mostRecent.timestamp).toISOString()}`
                );

                // Ensure we have QuoteId before processing
                if (!currentStateRef.current.QuoteId) {
                    console.log(
                        "⚠️ EcoProtector: QuoteId not available yet, will retry processing in 2 seconds"
                    );
                    setTimeout(() => {
                        console.log(
                            "🔄 EcoProtector: Retrying onComponents processing with QuoteId:",
                            currentStateRef.current.QuoteId
                        );
                        if (currentStateRef.current.QuoteId) {
                            handleOnComponents(mostRecent.data);
                        } else {
                            console.log(
                                "❌ EcoProtector: QuoteId still not available, skipping pending message processing"
                            );
                        }
                    }, 2000);
                } else {
                    handleOnComponents(mostRecent.data);
                }
            }

            if (pendingComplete.length === 0 && pendingComponents.length === 0) {
                console.log("✅ EcoProtector: No pending messages to process");
            }
        };

        // Check immediately
        checkPendingMessages();

        // Also check after a short delay to ensure handlers are fully registered and QuoteId is available
        const timeoutId = setTimeout(() => {
            console.log("🔍 EcoProtector: Second check for pending messages (after delay)");
            checkPendingMessages();
        }, 1000);

        // Final check after a longer delay to catch cases where QuoteId takes time to be set
        const finalTimeoutId = setTimeout(() => {
            console.log("🔍 EcoProtector: Final check for pending messages (after longer delay)");
            checkPendingMessages();
        }, 3000);

        return () => {
            clearTimeout(timeoutId);
            clearTimeout(finalTimeoutId);
            console.log("🧹 EcoProtector: Unregistering SignalR handlers");
            unregisterGlobalHandler("onComplete", HANDLER_ID);
            unregisterGlobalHandler("onComponents", HANDLER_ID);
        };
    }, [
        isConnected,
        registerGlobalHandler,
        unregisterGlobalHandler,
        getPendingMessages,
        handleOnComplete,
        handleOnComponents,
    ]);

    // Auto-save state when relevant data changes
    useEffect(() => {
        if (currentStage !== WORKFLOW_STAGES.CALCULATE_PRICE || isGenerating) {
            saveStateToLocalStorage();
        }
    }, [
        currentStage,
        activeStep,
        viewerPath,
        downloadUrls,
        modelConfig,
        totalPrice,
        priceBreakdown,
        isGenerating,
        saveStateToLocalStorage,
    ]);

    // Stage transition functions
    const moveToGenerateStage = useCallback(() => {
        setCurrentStage(WORKFLOW_STAGES.GENERATE_MODEL);
        setActiveStep(1);
    }, []);

    const moveToDownloadStage = useCallback(() => {
        setCurrentStage(WORKFLOW_STAGES.DOWNLOAD_AND_QUOTE);
        setActiveStep(2);
        setIsGenerating(false);
        sessionStorage.removeItem(STORAGE_KEY);
    }, []);

    // Reset workflow
    const resetWorkflow = useCallback(() => {
        setCurrentStage(WORKFLOW_STAGES.CALCULATE_PRICE);
        setActiveStep(0);
        setTotalPrice(0);
        setPriceBreakdown(null);
        setDownloadUrls({ pdf: "", dwg: "", dwg3d: "" });
        setViewerPath(`${process.env.REACT_APP_API_URL}/StdModel/output/1/result.svf`);
        setModelConfig((prev) => ({ ...prev, workItemId: "" }));
        setIsGenerating(false);
        sessionStorage.removeItem(STORAGE_KEY);
        showNotification("Reset to start new configuration", "info");
    }, [showNotification]);

    return {
        // State
        currentStage,
        activeStep,
        isGenerating,
        totalPrice,
        priceBreakdown,
        viewerPath,
        downloadUrls,
        modelConfig,

        // Setters
        setCurrentStage,
        setActiveStep,
        setIsGenerating,
        setTotalPrice,
        setPriceBreakdown,
        setViewerPath,
        setDownloadUrls,
        setModelConfig,

        // Functions

        moveToGenerateStage,
        moveToDownloadStage,
        resetWorkflow,
        saveStateToLocalStorage,
        restoreStateFromLocalStorage,

        // SignalR status
        isSignalRConnected: isConnected,
        signalRConnectionId: connectionId,
    };
};
